// Modal component styled for mfe
import React, { useCallback, useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import themeable from 'zignal-landscape/lib/themeable';
import ThemeProvider from 'zignal-landscape/lib/ThemeProvider';
import { Dark } from 'themes';
import classnames from 'classnames';
import noop from 'lodash/noop';
import { usePrevious } from 'react-use';
import MfeModal from '../../components/MfeModal';
import ToolTip from 'zignal-landscape/lib/ToolTip';
// icons
import Remove1Icon from 'zignal-landscape/lib/Icon/Remove1Icon';
import MfeDashDeleteIcon from 'zignal-landscape/lib/Icon/MfeDashDeleteIcon';
import MfeDashFav2Icon from 'zignal-landscape/lib/Icon/MfeDashFav2Icon';
import MfeDashUnFavIcon from 'zignal-landscape/lib/Icon/MfeDashUnFavIcon';
import Save1Icon from 'zignal-landscape/lib/Icon/Save1Icon';
import SearchInput from 'zignal-landscape/lib/SearchInput';
import MfeDashAllIcon from 'zignal-landscape/lib/Icon/MfeDashAllIcon';

const Width18 = () => <div style={{width: 18}} />;
const Width12 = () => <div style={{width: 12}} />;

const tooltip = (icon, text) => <ToolTip tooltipText={text}>{icon}</ToolTip>;

const DEFAULT_ITEMS = [];

const SelectionModal = ({
  styles,
  title = '',
  items = DEFAULT_ITEMS,
  filters = [{label: 'All', icon: MfeDashAllIcon }],
  dataType = null,
  defaultView = null,
  isCampaignAdmin = false,
  hideAddButton,
  onClose = noop,
  onClickItem = noop,
  onAddItem = noop,
  onDeleteItem = noop,
  onFavoriteItem = null
}) => {
  const [search, setSearch] = useState('');
  const prevSearch = usePrevious(search);
  const [selectedFilter, setSelectedFilter] = useState(filters[0]);

  // set the selected filter to the last filter (All) when a new search is performed
  useEffect(() => {
    if (filters.length > 1 && search && !prevSearch) {
      setSelectedFilter(filters[filters.length - 1]);
    }
  }, [filters, search, prevSearch, setSelectedFilter]);

  const onClickFilter = useCallback((filter) => setSelectedFilter(filter), [setSelectedFilter]);
  const memoItems = useMemo(() => {
    return selectedFilter.label === 'All' ? items : items.filter(({filterTypes}) => filterTypes.includes(selectedFilter.label));
  }, [selectedFilter, items]);

  let unsearchedItems = memoItems;
  if (defaultView && defaultView.filterTypes.includes(selectedFilter.label)) {
    unsearchedItems = [defaultView, ...unsearchedItems];
  }
  const searchedItems = search ? unsearchedItems.filter(({label, subtext}) => {
    return label.toLowerCase().includes(search.toLowerCase()) || subtext?.toLowerCase().includes(search.toLowerCase());
  }) : unsearchedItems;

  const hasFilters = filters.length > 1;
  const placeholder = `${title.slice(0, title.length - 1)}...`;

  const titleClassNames =  {
    [styles.SelectionModalTitle]: true,
    [styles.hideAddButton]: hideAddButton
  };

  return (<MfeModal
    isOpen
    size="medium"
    onBackdropClick={onClose}
  >
    <ThemeProvider theme={Dark}>
      <div className={styles.SelectionModal}>
        <div className={styles.SelectionModalHeader}>
          <div className={classnames(titleClassNames)}>{title}</div>
          { hideAddButton ? null : <div data-type={dataType} data-cy="add-new" className={styles.SelectionModalAdd} onClick={onAddItem}>+</div> }
          <div className={styles.SelectionModalSearch}><SearchInput data-cy="search-input" mfeMode placeholder={placeholder} onChange={setSearch} /></div>
          <div className={styles.SelectionModalCancel}><Remove1Icon data-cy="close-modal" onClick={onClose} /></div>
        </div>
        <div className={styles.SelectionModalFilterBar}>
          {filters.map((filter) => {
            const { label, icon: FilterIcon } = filter;
            const filterClassNames =  {
              [styles.SelectionModalFilter]: true,
              [styles.SelectionModalFilterActive]: selectedFilter.label === filter.label
            };
            return (<div key={label} className={classnames(filterClassNames)} onClick={filters.length === 1 ? noop : () => onClickFilter(filter)}>
              <FilterIcon height={16} width={16} />
              <span className={styles.SelectionModalFilterText}>{label}</span>
            </div>);
          })}
        </div>
        <div className={styles.SelectionModalBody}>
          {searchedItems.map((item) => {
            const { id, label, subtext, active, icon: FilterIcon, href, isFavorited, isOwner, model } = item;
            const itemClassNames = {
              [styles.SelectionModalItem]: true,
              [styles.SelectionModalItemActive]: active,
              [styles.SelectionModalItemWithSubtext]: subtext !== undefined,
            };
            const showFavoriteAction = !!onFavoriteItem && isCampaignAdmin;
            const showDeleteAction = isOwner;
            const showActions = id !== 'default' && (showFavoriteAction || showDeleteAction);
            return (
              <div key={id} className={styles.SelectionModalItemContainer}>
                <a href={href} key={id} className={classnames(itemClassNames)} onClick={(e) => onClickItem(e, model)}>
                  { active ? <Save1Icon height={18} width={18} /> : <Width18 /> }
                  { FilterIcon ? <FilterIcon height={18} width={18} /> : hasFilters ? <Width12 /> : null }
                  <span className={styles.SelectionModalItemText}>{label}</span>
                  { subtext ? <div className={styles.SelectionModalItemSubtext}>{subtext}</div> : null }
                </a>
                { showActions && <div className={styles.SelectionModalItemActions}>
                  { showFavoriteAction &&
                  <div
                    data-cy="selection-modal-favorite-icon"
                    className={styles.SelectionModalItemAction}
                    onClick={(e) => onFavoriteItem(e, item)}
                  >
                    { isFavorited
                      ? tooltip(<MfeDashUnFavIcon height={22} width={22} />, 'Unfavorite')
                      : tooltip(<MfeDashFav2Icon height={22} width={22} />, 'Favorite')
                    }
                  </div>
                  }
                  { showDeleteAction &&
                  <div
                    data-cy="selection-modal-delete-icon"
                    className={styles.SelectionModalItemAction}
                    onClick={(e) => onDeleteItem(e, item)}
                  >
                    { tooltip(<MfeDashDeleteIcon height={22} width={22} />, 'Delete') }
                  </div>
                  }
                </div>
                }
              </div>
            );
          })}
        </div>
      </div>
    </ThemeProvider>
  </MfeModal>);
};

SelectionModal.propTypes = {
  styles: PropTypes.object.isRequired,
  title: PropTypes.string,
  items: PropTypes.array,
  filters: PropTypes.array,
  dataType: PropTypes.string,
  defaultView: PropTypes.object,
  isCampaignAdmin: PropTypes.bool,
  onClose: PropTypes.func,
  onClickItem: PropTypes.func,
  onAddItem: PropTypes.func,
  onDeleteItem: PropTypes.func,
  onFavoriteItem: PropTypes.func
};

export default themeable(SelectionModal);
